import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { closestGradeBadge } from '@/lib/closestGrade';

export const FEATURE_META: FeatureMeta = {
  id: 'curated-offer',
  label: 'Prepare offer',
  description: 'Pick which of your fitting listings to offer a buyer for their Buy Request; Cannafax writes one offer you review before sending.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller'],
};

export interface CuratedOfferItem { id: string; title: string; specs: string; closestTo?: string | null; form?: string | null }

const TOP = 3;

export default function CuratedOfferDialog({ open, onOpenChange, buyer, want, items, onContinue }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  buyer: string;
  want: string;
  items: CuratedOfferItem[];
  /** Hands the chosen listings + note to the message composer. Nothing is sent from here. */
  onContinue: (chosen: CuratedOfferItem[], note: string) => void;
}) {
  useFeatureRegistration(FEATURE_META);
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  useEffect(() => { if (open) { setPicked(items.slice(0, TOP).map((i) => i.id)); setNote(''); } }, [open, items]);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const chosen = items.filter((i) => picked.includes(i.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Fixed frame: header and footer stay put; only the listing list scrolls. */}
      <DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[min(720px,calc(100dvh-2rem))] overflow-hidden">
        <div className="p-6 pb-3 space-y-3 border-b">
          <DialogHeader>
            <DialogTitle>Offer {buyer} what you have</DialogTitle>
            <DialogDescription>“{want}” — choose the listings to include. You'll see the full message before it goes.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground"><span className="num text-foreground">{chosen.length}</span> of <span className="num">{items.length}</span> selected</span>
            {items.length > TOP && (
              <span className="flex gap-1">
                <Button size="sm" variant="quiet" onClick={() => setPicked(items.slice(0, TOP).map((i) => i.id))}>Top {TOP}</Button>
                <Button size="sm" variant="quiet" onClick={() => setPicked([])} disabled={chosen.length === 0}>Clear</Button>
              </span>
            )}
          </div>
        </div>
        <ul className="flex-1 min-h-0 overflow-y-auto overscroll-contain divide-y">
          {items.map((i) => (
            <li key={i.id}>
              <label className="flex items-start gap-3 px-6 py-3 cursor-pointer hover:bg-muted/40">
                <Checkbox checked={picked.includes(i.id)} onCheckedChange={() => toggle(i.id)} className="mt-0.5" aria-label={`Include ${i.title}`} />
                <span className="min-w-0">
                  <span className="font-medium">{i.title}</span>
                  {i.closestTo && <Badge className="ml-2" variant="outline">{closestGradeBadge()}</Badge>}
                  {i.specs && <span className="block text-sm text-muted-foreground">{i.specs}</span>}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <div className="p-4 sm:px-6 border-t space-y-3 bg-background">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional) — e.g. delivery this week, price holds through Friday" rows={2} className="resize-none" />
          <div className="flex justify-end gap-2">
            <Button variant="quiet" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={chosen.length === 0} onClick={() => onContinue(chosen, note)}>
              Review offer ({chosen.length})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
